import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import { isStaffRole } from '../lib/adminAccess';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check for existing session on mount
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        let active = true;

        const restoreSession = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const response = await authApi.getMe();
                if (!active) return;
                setUser(response.data);
                setIsAuthenticated(true);
                localStorage.setItem('user', JSON.stringify(response.data));
            } catch {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
            } finally {
                if (active) setLoading(false);
            }
        };

        restoreSession();
        return () => { active = false; };
    }, []);

    const login = useCallback(async (email, password) => {
        const response = await authApi.login({ email, password });
        const { access_token, user: userData } = response.data;
        
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return userData;
    }, []);

    const register = useCallback(async (data) => {
        const response = await authApi.register(data);
        return response.data;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
    }, []);

    const updateUser = useCallback((userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const response = await authApi.getMe();
            const userData = response.data;
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            return userData;
        } catch (e) {
            if (e.response?.status === 401) logout();
            throw e;
        }
    }, [logout]);

    const value = {
        user,
        loading,
        isAuthenticated,
        isAdmin: user?.role === 'admin',
        isStaff: isStaffRole(user?.role, user?.forum_role),
        isCustomer: user?.role === 'customer',
        login,
        register,
        logout,
        updateUser,
        refreshUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
